import { ComponentPropsWithoutRef, ReactNode } from 'react';

import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/utils/tailwind';

type ButtonOwnProps = VariantProps<typeof buttonVariants> & {
  as?: 'button' | 'a';
  asChild?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  disabled?: boolean;
};

type ButtonProps =
  | (ButtonOwnProps &
      Omit<ComponentPropsWithoutRef<'button'>, keyof ButtonOwnProps> & {
        as?: 'button';
      })
  | (ButtonOwnProps &
      Omit<ComponentPropsWithoutRef<'a'>, keyof ButtonOwnProps> & {
        as: 'a';
      });

const buttonVariants = cva(
  'text-body-14-semibold inline-flex cursor-pointer items-center justify-center gap-2.5 whitespace-nowrap rounded-[6px] transition-all disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive',
  {
    variants: {
      variant: {
        primary:
          'bg-custom-cyan-500 shadow-sm !text-custom-warm-grey-25 hover:bg-custom-cyan-600 focus:bg-custom-cyan-600 focus:outline focus:outline-custom-cyan-800 disabled:bg-custom-cyan-200 disabled:!text-custom-warm-grey-25',
        secondary:
          'bg-custom-warm-grey-25 shadow-sm [&_svg]:text-custom-warm-grey-500 !text-custom-warm-grey-700 !outline outline-custom-warm-grey-200 hover:bg-custom-warm-grey-100 focus:bg-custom-warm-grey-100 focus:outline-custom-warm-grey-400 disabled:bg-custom-warm-grey-100 disabled:outline-custom-warm-grey-200 disabled:!text-custom-warm-grey-300',
        'teritary-bright':
          'bg-transparent [&_svg]:text-custom-warm-grey-500 !text-custom-warm-grey-700 hover:bg-custom-warm-grey-100 focus:bg-custom-warm-grey-150 disabled:!text-custom-warm-grey-300 disabled:[&_svg]:text-custom-warm-grey-300',
        teritary:
          'bg-transparent [&_svg]:text-custom-warm-grey-400 !text-custom-warm-grey-500 hover:bg-custom-warm-grey-100 focus:bg-custom-warm-grey-150 disabled:!text-custom-warm-grey-300 disabled:[&_svg]:text-custom-warm-grey-300',
        destructive:
          'bg-custom-red-600 shadow-sm !text-custom-warm-grey-25 hover:bg-custom-red-700 focus:outline focus:outline-custom-red-900 disabled:bg-custom-red-200 disabled:!text-custom-warm-grey-25',
      },
      size: {
        xxs: 'h-6 py-[5px] px-2 text-body-12-semibold',
        xs: 'h-7 py-[5px] px-2',
        sm: 'h-8 py-[7px] px-2.5',
        md: 'h-9 py-[9px] px-3',
        lg: 'h-10 py-[11px] px-3.5',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  }
);

function Button({
  className,
  variant,
  size,
  children,
  leftIcon,
  rightIcon,
  as,
  disabled,
  ...props
}: ButtonProps) {
  if (as === 'a') {
    const anchorProps = props as Omit<ComponentPropsWithoutRef<'a'>, keyof ButtonOwnProps>;

    return (
      <a
        data-slot="button"
        className={cn(buttonVariants({ variant, size, className }))}
        {...anchorProps}
      >
        {leftIcon && <span className="flex items-center">{leftIcon}</span>}
        {children}
        {rightIcon && <span className="flex items-center">{rightIcon}</span>}
      </a>
    );
  }

  return (
    <button
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      disabled={disabled}
      {...(props as Omit<ComponentPropsWithoutRef<'button'>, keyof ButtonOwnProps>)}
    >
      {leftIcon && <span className="flex items-center">{leftIcon}</span>}
      {children}
      {rightIcon && <span className="flex items-center">{rightIcon}</span>}
    </button>
  );
}

export { Button };
